import type {NormPoint} from './protocol';

/**
 * Describes where the decoded video frame is drawn inside the &lt;video&gt; element
 * for the current `object-fit` / `object-position` (CSS replaced-content rules).
 * Coordinates are in CSS pixels relative to the video element's border box (top-left origin).
 */
export type VideoRenderBox = {
    rect: DOMRect;
    renderWidth: number;
    renderHeight: number;
    offsetX: number;
    offsetY: number;
};

/** Resolve object-position keywords / % into 0–1 alignment (CSS Images 3). */
function parseObjectPosition(pos: string): {x: number; y: number} {
    const s = pos.trim().toLowerCase();
    if (!s) {
        return {x: 0.5, y: 0.5};
    }

    const kw = (p: string): number | null => {
        switch (p) {
            case 'left':
                return 0;
            case 'right':
                return 1;
            case 'top':
                return 0;
            case 'bottom':
                return 1;
            case 'center':
                return 0.5;
            default:
                return null;
        }
    };

    const parseAtom = (p: string): number | null => {
        const k = kw(p);
        if (k !== null) {
            return k;
        }
        if (p.endsWith('%')) {
            const n = parseFloat(p);
            return Number.isFinite(n) ? n / 100 : null;
        }
        return null;
    };

    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
        const a = parseAtom(parts[0]!);
        if (a !== null) {
            return {x: a, y: a};
        }
        return {x: 0.5, y: 0.5};
    }

    const x = parseAtom(parts[0]!) ?? 0.5;
    const y = parseAtom(parts[1]!) ?? 0.5;
    return {x, y};
}

/**
 * Shared by sender and receiver: maps pointer position to normalized coords (0–1) within the
 * **visible video content** (not the full element box).
 */
export function getVideoRenderBox(video: HTMLVideoElement): VideoRenderBox {
    const rect = video.getBoundingClientRect();
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const W = rect.width;
    const H = rect.height;

    if (!vw || !vh || W <= 0 || H <= 0) {
        return {
            rect,
            renderWidth: Math.max(1, W),
            renderHeight: Math.max(1, H),
            offsetX: 0,
            offsetY: 0,
        };
    }

    const style = getComputedStyle(video);
    const objectFit = style.objectFit || 'fill';
    const {x: posX, y: posY} = parseObjectPosition(style.objectPosition || '50% 50%');

    let renderWidth: number;
    let renderHeight: number;
    let offsetX: number;
    let offsetY: number;

    switch (objectFit) {
        case 'fill': {
            renderWidth = W;
            renderHeight = H;
            offsetX = 0;
            offsetY = 0;
            break;
        }
        case 'none': {
            renderWidth = vw;
            renderHeight = vh;
            offsetX = (W - renderWidth) * posX;
            offsetY = (H - renderHeight) * posY;
            break;
        }
        case 'contain': {
            const scale = Math.min(W / vw, H / vh);
            renderWidth = vw * scale;
            renderHeight = vh * scale;
            offsetX = (W - renderWidth) * posX;
            offsetY = (H - renderHeight) * posY;
            break;
        }
        case 'cover': {
            const scale = Math.max(W / vw, H / vh);
            renderWidth = vw * scale;
            renderHeight = vh * scale;
            offsetX = (W - renderWidth) * posX;
            offsetY = (H - renderHeight) * posY;
            break;
        }
        case 'scale-down': {
            const scaleContain = Math.min(W / vw, H / vh);
            const scaleNone = 1;
            const scale = Math.min(scaleContain, scaleNone);
            renderWidth = vw * scale;
            renderHeight = vh * scale;
            offsetX = (W - renderWidth) * posX;
            offsetY = (H - renderHeight) * posY;
            break;
        }
        default: {
            const scale = Math.min(W / vw, H / vh);
            renderWidth = vw * scale;
            renderHeight = vh * scale;
            offsetX = (W - renderWidth) * posX;
            offsetY = (H - renderHeight) * posY;
            break;
        }
    }

    return {rect, renderWidth, renderHeight, offsetX, offsetY};
}

/**
 * Pointer → normalized coordinates in video content space (0–1), clamped.
 */
export function normFromClientInVideo(
    video: HTMLVideoElement,
    clientX: number,
    clientY: number
): NormPoint {
    const {rect, renderWidth, renderHeight, offsetX, offsetY} = getVideoRenderBox(video);
    let x = (clientX - rect.left - offsetX) / renderWidth;
    let y = (clientY - rect.top - offsetY) / renderHeight;
    x = Math.max(0, Math.min(1, x));
    y = Math.max(0, Math.min(1, y));
    return {x, y};
}

/** Enable faint debug outline via `localStorage.setItem('annotationDebug','1')`. */
export function isAnnotationDebug(): boolean {
    try {
        return typeof localStorage !== 'undefined' && localStorage.getItem('annotationDebug') === '1';
    } catch {
        return false;
    }
}
